import SiteNavBar from '../../components/feature/SiteNavBar';
import SiteFooter from '../../components/feature/SiteFooter';
import { propertyData } from '../../mocks/property';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

function SpecCard({ spec }: { spec: { category: string; items: { label: string; value: string }[] } }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`reveal-up ${isVisible ? 'visible' : ''} bg-white border border-[#E5DDD3] p-8`}>
      <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#B59A6A] mb-6">{spec.category}</p>
      <div className="space-y-4">
        {spec.items.map((item) => (
          <div key={item.label} className="flex justify-between items-baseline border-b border-[#F0EDE8] pb-3">
            <span className="font-sans text-[12px] text-[#6B6460]">{item.label}</span>
            <span className="font-sans text-[13px] font-medium text-[#1C1C1C]">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HighlightsPage() {
  const { t } = useTranslation();
  const { ref: refHead, isVisible: visHead } = useScrollReveal();
  const { ref: refMat, isVisible: visMat } = useScrollReveal();
  const { ref: refFloor, isVisible: visFloor } = useScrollReveal();

  const detailedSpecs = [
    { category: t('spec.cat.area'), items: [
      { label: t('spec.totalArea'), value: '680 m²' },
      { label: t('spec.usableArea'), value: '820 m²' },
      { label: t('spec.plot'), value: '2.400 m²' },
    ]},
    { category: t('spec.cat.rooms'), items: [
      { label: t('spec.bedrooms'), value: '6' },
      { label: t('spec.bathrooms'), value: t('spec.val.bathrooms') },
      { label: t('spec.guestRooms'), value: '2' },
    ]},
    { category: t('spec.cat.features'), items: [
      { label: t('spec.pool'), value: t('spec.val.pool') },
      { label: t('spec.garage'), value: t('spec.val.garage') },
      { label: t('spec.wellness'), value: t('spec.val.wellness') },
    ]},
    { category: t('spec.cat.quality'), items: [
      { label: t('spec.year'), value: '2021' },
      { label: t('spec.energyClass'), value: 'AAA' },
      { label: t('spec.heating'), value: t('spec.val.heating') },
    ]},
  ];

  const materials = [
    { name: t('mat.flooring'), value: 'Calacatta Marmor & Eichenparkett', icon: 'ri-layout-grid-line' },
    { name: t('mat.kitchen'), value: 'Bulthaup · Brass & Oak Bespoke', icon: 'ri-knife-line' },
    { name: t('mat.bathroom'), value: 'Gessi Armaturenreihe · Handgemacht', icon: 'ri-drop-line' },
    { name: t('mat.smarthome'), value: 'Siemens Loxone Hausautomation', icon: 'ri-settings-4-line' },
    { name: t('mat.security'), value: '24/7 Smart Security System', icon: 'ri-shield-check-line' },
    { name: t('mat.glazing'), value: 'Schüco · Dreifach Wärmeschutz', icon: 'ri-window-line' },
  ];

  const highlightItems = propertyData.highlights.map((item) => ({
    ...item,
    label: item.label.startsWith('prop.') ? t(item.label) : item.label,
    value: item.value.startsWith('prop.') ? t(item.value) : item.value,
  }));

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <SiteNavBar />
      <section className="relative h-72 flex items-end pb-14 overflow-hidden">
        <img src="https://public.readdy.ai/ai/img_res/a86c42b06764e1799fa94806aaaf8c06.jpg" alt="Highlights Hero" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
          <p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A] mb-3">The Obsidian Villa</p>
          <h1 className="font-serif text-4xl md:text-5xl font-medium text-white">{t('nav.highlights')}</h1>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-8">
          <div ref={refHead} className={`mb-14 reveal-up ${visHead ? 'visible' : ''}`}>
            <div className="flex items-center gap-4 mb-4"><div className="gold-line" /><p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('highlights.page.label')}</p></div>
            <h2 className="font-serif text-3xl font-medium text-[#1C1C1C]">{t('highlights.page.essentials')}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5DDD3]">
            {highlightItems.map((item) => (
              <div key={item.label} className="bg-[#FAFAF7] hover:bg-[#F5F0E8] transition-colors duration-300 p-10 text-center group">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4"><i className={`${item.icon} text-2xl text-[#B59A6A]`} /></div>
                <p className="font-serif text-xl font-medium text-[#1C1C1C] mb-1">{item.value}</p>
                <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#6B6460]">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center gap-4 mb-12"><div className="gold-line" /><p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('highlights.page.techLabel')}</p></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {detailedSpecs.map((spec) => <SpecCard key={spec.category} spec={spec} />)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-8">
          <div ref={refMat} className={`mb-14 reveal-up ${visMat ? 'visible' : ''}`}>
            <div className="flex items-center gap-4 mb-4"><div className="gold-line" /><p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('highlights.page.materialsLabel')}</p></div>
            <h2 className="font-serif text-3xl font-medium text-[#1C1C1C]">{t('highlights.page.materialsHeading')}</h2>
            <p className="font-sans text-sm text-[#6B6460] mt-3 font-light max-w-lg">{t('highlights.page.materialsSub')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {materials.map((mat) => (
              <div key={mat.name} className="flex items-start gap-5 p-7 border border-[#E5DDD3] bg-white hover:border-[#B59A6A]/40 transition-colors duration-300 group">
                <div className="w-10 h-10 flex items-center justify-center shrink-0"><i className={`${mat.icon} text-xl text-[#B59A6A]`} /></div>
                <div>
                  <p className="font-sans text-[10px] tracking-widest uppercase text-[#B59A6A] mb-1">{mat.name}</p>
                  <p className="font-sans text-sm text-[#1C1C1C] font-light">{mat.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F5F3EF]">
        <div className="max-w-7xl mx-auto px-8">
          <div ref={refFloor} className={`mb-12 reveal-up ${visFloor ? 'visible' : ''}`}>
            <div className="flex items-center gap-4 mb-4"><div className="gold-line" /><p className="font-sans text-[10px] tracking-[0.35em] uppercase text-[#B59A6A]">{t('highlights.page.floorplanLabel')}</p></div>
            <h2 className="font-serif text-3xl font-medium text-[#1C1C1C]">{t('highlights.page.floorplanHeading')}</h2>
          </div>
          <div className="border border-[#E5DDD3] bg-white p-16 flex flex-col items-center justify-center min-h-[400px]">
            <div className="w-16 h-16 flex items-center justify-center border border-[#E5DDD3] mb-6"><i className="ri-layout-4-line text-2xl text-[#B59A6A]" /></div>
            <h3 className="font-serif text-2xl font-medium text-[#1C1C1C] mb-3">{t('highlights.page.floorplanHeading')}</h3>
            <p className="font-sans text-sm text-[#6B6460] font-light text-center max-w-sm mb-8">{t('highlights.page.floorplanDesc')}</p>
            <a href="/kontakt" className="gold-btn text-[10px] tracking-widest">{t('highlights.page.floorplanBtn')}</a>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
